import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { createConfig, getEnum, setAuth0Methods } from '../../api';
import { useNotification } from '../../context/NotificationContext';

const AddConfigPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user, logout, getAccessTokenSilently } = useAuth0();

  // Set auth methods for API calls
  useEffect(() => {
    setAuth0Methods({
      getAccessTokenSilently,
      loginWithRedirect: () => window.location.href = '/login'
    });
  }, [getAccessTokenSilently]);
  const [config, setConfig] = useState({
    name: '',
    client_id: '',
    client_type_id: '',
    status: 'active',
    description: '',
    elevenlabs_model: '',
    elevenlabs_voice_id: '',
    client_internal_id: '',
    twilio_phone_number: '',
    twilio_phone_number_sid: '',
    about_us: '',
    services: '',
    opening_line: '',
    prompt: '',
  });
  const [clientTypes, setClientTypes] = useState([]);

  useEffect(() => {
    const fetchClientTypes = async () => {
      try {
        const data = await getEnum('client-type');
        setClientTypes(data);
        if (data.length > 0) {
          setConfig(prevConfig => ({ ...prevConfig, client_type_id: data[0].id }));
        }
      } catch (error) {
        console.error("Error fetching client types:", error);
      }
    };
    fetchClientTypes();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfig({ ...config, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!config.name || !config.client_id || !config.client_type_id) {
      showNotification('Please fill in all required fields (Client Name, Deployment ID, Client Type).', 'error');
      return;
    }
    
    try {
      // Prepare data matching the original API format
      const configData = {
        name: config.name,
        client_id: config.client_id,
        client_type_id: parseInt(config.client_type_id),
        elevenlabs_model: config.elevenlabs_model || undefined,
        elevenlabs_voice_id: config.elevenlabs_voice_id || undefined,
        client_internal_id: config.client_internal_id || undefined,
        twilio_phone_number: config.twilio_phone_number || undefined,
        twilio_phone_number_sid: config.twilio_phone_number_sid || undefined,
        about_us: config.about_us || undefined,
        services: config.services || undefined,
        opening_line: config.opening_line || undefined,
        prompt: config.prompt || undefined,
      };
      
      await createConfig(configData);
      showNotification('Configuration created successfully!', 'success');
      navigate('/');
    } catch (error) {
      showNotification(error.message, 'error');
      console.error("Error creating configuration:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with user info and logout */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center transition-colors duration-200 shadow-sm"
              >
                ← Back
              </button>
              <div>
                <h1 className="text-2xl font-bold text-purple-600">Add Client Configuration</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user?.name}</div>
                <div className="text-sm text-gray-500">{user?.email}</div>
              </div>
              <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-purple-600">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <button
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-md hover:bg-gray-100 transition duration-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6 pb-24">
        <div className="max-w-6xl mx-auto">

        <form onSubmit={handleSubmit}>
          <div className="section-card">
            <div className="section-header">
              <h2 className="text-xl font-semibold">New Configuration Details</h2>
            </div>
            <div className="section-content p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="input-group">
                  <label htmlFor="name" className="form-label">Client Name</label>
                  <input type="text" id="name" name="name" value={config.name} onChange={handleInputChange} className="form-input" required placeholder="Enter Client Name" />
                </div>
                <div className="input-group">
                  <label htmlFor="client_id" className="form-label">Deployment ID (UUID)</label>
                  <input type="text" id="client_id" name="client_id" value={config.client_id} onChange={handleInputChange} className="form-input" required placeholder="Enter UUID" />
                </div>
              </div>
              
              <div className="input-group">
                <label htmlFor="client_type_id" className="form-label">Client Type</label>
                <select id="client_type_id" name="client_type_id" value={config.client_type_id} onChange={handleInputChange} className="form-input" required>
                  {clientTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="input-group">
                  <label htmlFor="elevenlabs_model" className="form-label">ElevenLabs Model</label>
                  <input type="text" id="elevenlabs_model" name="elevenlabs_model" value={config.elevenlabs_model} onChange={handleInputChange} className="form-input" placeholder="Enter Model" />
                </div>
                <div className="input-group">
                  <label htmlFor="elevenlabs_voice_id" className="form-label">ElevenLabs Voice ID</label>
                  <input type="text" id="elevenlabs_voice_id" name="elevenlabs_voice_id" value={config.elevenlabs_voice_id} onChange={handleInputChange} className="form-input" placeholder="Enter Voice ID" />
                </div>
              </div>
              
              <div className="input-group">
                <label htmlFor="client_internal_id" className="form-label">Client Internal ID</label>
                <input type="text" id="client_internal_id" name="client_internal_id" value={config.client_internal_id} onChange={handleInputChange} className="form-input" placeholder="Enter Internal ID" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="input-group">
                  <label htmlFor="twilio_phone_number" className="form-label">Twilio Phone Number</label>
                  <input type="text" id="twilio_phone_number" name="twilio_phone_number" value={config.twilio_phone_number} onChange={handleInputChange} className="form-input" placeholder="Enter Twilio Phone Number" />
                </div>
                <div className="input-group">
                  <label htmlFor="twilio_phone_number_sid" className="form-label">Twilio Phone Number SID</label>
                  <input type="text" id="twilio_phone_number_sid" name="twilio_phone_number_sid" value={config.twilio_phone_number_sid} onChange={handleInputChange} className="form-input" placeholder="Enter Twilio Phone Number SID" />
                </div>
              </div>
              
              <div className="input-group">
                <label htmlFor="about_us" className="form-label">About Us</label>
                <textarea id="about_us" name="about_us" value={config.about_us} onChange={handleInputChange} className="form-input" rows="3" placeholder="Enter About Us"></textarea>
              </div>
              
              <div className="input-group">
                <label htmlFor="services" className="form-label">Services</label>
                <textarea id="services" name="services" value={config.services} onChange={handleInputChange} className="form-input" rows="3" placeholder="Enter Services"></textarea>
              </div>
              
              <div className="input-group">
                <label htmlFor="opening_line" className="form-label">Opening Line</label>
                <textarea id="opening_line" name="opening_line" value={config.opening_line} onChange={handleInputChange} className="form-input" rows="2" placeholder="Enter Opening Line for AI"></textarea>
              </div>
              
              <div className="input-group">
                <label htmlFor="prompt" className="form-label">Prompt</label>
                <textarea id="prompt" name="prompt" value={config.prompt} onChange={handleInputChange} className="form-input" rows="5" placeholder="Enter Main Prompt for AI"></textarea>
              </div>
              
              <div className="input-group">
                <label htmlFor="status" className="form-label">Status</label>
                <select id="status" name="status" value={config.status} onChange={handleInputChange} className="form-input">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button type="submit" className="update-button">
              Create Configuration
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default AddConfigPage; 